import React, { useState, useEffect, KeyboardEvent, useRef, FC } from 'react';
import { Box, Typography, Popover } from '@mui/material';
import { SubdirectoryArrowLeft } from '@mui/icons-material';
import { MultilineFormTextField } from '../../../NewUI/Components/CustomUIElements/FormTextField';
import { classes } from './styles';

interface FeedbackInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  placeholder?: string;
  suggestedPrompts?: string[];
  onChipClick?: (chip: string) => void;
  showChipsCallback?: (showChips: boolean) => void;
  isLoading?: boolean;
  language?: 'english' | 'french';
}

export const FeedbackInput: FC<FeedbackInputProps> = ({
  value,
  onChange,
  onSubmit,
  placeholder = 'Ask Felix to refine results here',
  suggestedPrompts = [],
  onChipClick,
  showChipsCallback,
  isLoading = false,
  language = 'english'
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLTextAreaElement | null>(null);
  const [popoverText, setPopoverText] = useState('');
  const textInputRef = useRef<HTMLInputElement>(null);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      setPopoverText(value);
      onSubmit(value);
      setAnchorEl(e.currentTarget);
      onChange('');
    }
  };

  const handleChipClick = (chip: string) => {
    if (textInputRef.current) {
      setAnchorEl(textInputRef.current as unknown as HTMLTextAreaElement);
      setPopoverText(chip);
      onSubmit(chip);
      onChange('');
      textInputRef.current.blur();
    }
  };

  useEffect(() => {
    if (popoverText.length > 0) {
      const timer = setTimeout(() => {
        setAnchorEl(null);
        setPopoverText('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [popoverText]);

  return (
    <Box sx={{ marginTop: '8px' }}>
      <MultilineFormTextField
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        fullWidth
        rows={1}
        styles={{ padding: 0 }}
        onKeyDown={handleKeyDown}
        draggable={isLoading}
        chips={suggestedPrompts}
        onChipClick={onChipClick || handleChipClick}
        showChipsCallback={showChipsCallback}
        disableResize
        innerRef={textInputRef}
      />
      <Popover
        open={!!popoverText}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        sx={classes.popover}
        slotProps={{
          paper: {
            sx: {
              marginTop: '-8px'
            }
          }
        }}
      >
        <Typography color="#2D388D" variant="body2">
          {popoverText}
        </Typography>
      </Popover>
      <Typography variant="body2" color="text.secondary" sx={classes.feedbackInputHelperText}>
        {language === 'french' ? (
          'Appuyez sur Entrée ou Retour <SubdirectoryArrowLeft /> pour agir'
        ) : (
          <>
            Press Enter or Return <SubdirectoryArrowLeft sx={{ fontSize: '14px' }} /> for action
          </>
        )}
      </Typography>
    </Box>
  );
};
